import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Loader2, Trophy, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../src/lib/store';
import { cn } from '../../src/lib/utils';
import { useSupabase } from '../../src/lib/SupabaseContext';
import { useMerchant } from '../../src/lib/hooks/useMerchant';
import { findLatticeBenchmark, MIN_LATTICE_REPORTS } from '../../src/lib/latticeBenchmark';
import { resolveCalibration } from '../../src/lib/calibration';

interface GhostPulseProps {
  itemId: string;
  slug?: string;
  currentPrice: number;
  onToggleExpand?: (isExpanded: boolean) => void;
  isExpanded?: boolean;
}

// The one Lattice price status a basket item shows: a cheaper price nearby
// (sparkle), a price way off the usual one (amber check), or a price that
// beats the Lattice (trophy). Never more than one.
export type LatticeItemStatus = 'deal' | 'check' | 'beater' | null;

export const getLatticeItemStatus = (
  item: { price: number; betterDeal?: unknown; priceCheck?: unknown; latticeRecord?: number | null; latticeReports?: number | null } | undefined,
  isUltra: boolean
): LatticeItemStatus => {
  if (!item || !isUltra) return null;
  if (item.betterDeal) return 'deal';
  if (item.priceCheck) return 'check';
  // A Lattice-Beater needs a record backed by enough shoppers; an unknown count
  // (a database without confidence scoring) keeps the old behaviour.
  const enoughReports = item.latticeReports == null || item.latticeReports >= MIN_LATTICE_REPORTS;
  if (item.latticeRecord != null && enoughReports && item.price > 0 && item.latticeRecord > item.price + 0.005) return 'beater';
  return null;
};

export const GhostPulse: React.FC<GhostPulseProps> = ({ itemId, slug, currentPrice, onToggleExpand, isExpanded }) => {
  const { t } = useTranslation();
  const userProfile = useStore(state => state.userProfile);
  const isUltraUser = useStore(state => state.isUltraUser);
  const updateItem = useStore(state => state.updateItem);
  const operatingContext = useStore(state => state.operatingContext);
  const items = useStore(state => (operatingContext === 'personnel' ? state.latticeItems : state.items));
  
  const [isScanning, setIsScanning] = useState(false);
  const supabase = useSupabase();
  const { userCoords, currentStore } = useMerchant();
  
  // CRITICAL: Feature Gate - Only Ultra users see the Pulse
  const isUltra = !!isUltraUser || !!userProfile?.is_ultra;

  // Directly find the item to ensure we have the absolute latest state
  const itemInStore = items.find(i => i.id === itemId);
  const status = getLatticeItemStatus(itemInStore, isUltra);

  const checkLattice = useCallback(async (ignore: boolean = false) => {
    if (ignore) return;
    
    // Use latest state to avoid closure issues
    const latestState = useStore.getState();
    const isPersonnel = latestState.operatingContext === 'personnel';
    const latestItem = (isPersonnel ? latestState.latticeItems : latestState.items).find(i => i.id === itemId);
    const latestPrice = latestItem?.price ?? currentPrice;
    const latestSlug = latestItem?.canonicalId ?? slug;

    if (!isUltra) {
      if (latestItem?.betterDeal || latestItem?.latticeRecord || latestItem?.priceCheck) {
        updateItem(itemId, { betterDeal: null, latticeRecord: null, latticeReports: null, priceCheck: null });
      }
      return;
    }

    if (!latestSlug) return;

    setIsScanning(true);
    console.log(`[GhostPulse] Scanning Lattice: ${latestSlug} @ ${latestPrice}`);

    try {
      const lat = userCoords.current?.lat || currentStore?.coordinates?.lat;
      const lng = userCoords.current?.lng || currentStore?.coordinates?.lng;

      const { radiusMiles: radius, horizonHours: horizon } = resolveCalibration(userProfile?.settings?.intelligence);

      // Evict cached deals that are older than the lookback horizon.
      // Prevents stale Zustand-persisted deals from showing after a DB entry is removed.
      const dealDate = latestItem?.betterDeal?.date;
      if (dealDate) {
        const dealAgeHours = (Date.now() - new Date(dealDate).getTime()) / (1000 * 60 * 60);
        if (dealAgeHours > horizon) {
          updateItem(itemId, { betterDeal: null, latticeRecord: null, latticeReports: null, priceCheck: null });
          return; // Skip re-query this tick; next cycle starts with a clean slate.
        }
      }

      const { latticeRecord, latticeReports, betterDeal: newDeal, priceCheck } = await findLatticeBenchmark(
        supabase,
        { ...(latestItem || {}), price: latestPrice, canonicalId: latestSlug } as any,
        { lat, lng, radiusMiles: radius, horizonHours: horizon }
      );

      if (ignore) return;

      if (priceCheck) {
        if (latestItem?.priceCheck?.usualPrice !== priceCheck.usualPrice || latestItem?.betterDeal || latestItem?.latticeRecord) {
          updateItem(itemId, { betterDeal: null, latticeRecord: null, priceCheck });
        }
        return;
      }
      if (latestItem?.priceCheck) updateItem(itemId, { priceCheck: null });

      if (latticeRecord === null) {
        if (latestItem?.betterDeal || latestItem?.latticeRecord) {
          updateItem(itemId, { betterDeal: null, latticeRecord: null, latticeReports: null });
        }
      } else if (newDeal) {
        const isNewDeal = !latestItem?.betterDeal ||
                         latestItem.betterDeal.price !== newDeal.price ||
                         latestItem.betterDeal.storeName !== newDeal.storeName ||
                         (latestItem.betterDeal.reports ?? null) !== newDeal.reports;

        if (isNewDeal) {
          console.log(`[GhostPulse] Updating deal for ${itemId}: $${latticeRecord}`);
          updateItem(itemId, { latticeRecord, latticeReports });
          // Small delay to ensure state update before setting deal
          setTimeout(() => updateItem(itemId, { betterDeal: newDeal }), 50);
        }
      } else if (latestItem?.betterDeal) {
        updateItem(itemId, { betterDeal: null, latticeRecord, latticeReports });
      } else {
        updateItem(itemId, { latticeRecord, latticeReports });
      }
    } catch (e) {
      console.error("[GhostPulse] Lattice check failed:", e);
    } finally {
      setIsScanning(false);
    }
  }, [itemId, slug, currentPrice, isUltra, supabase, userCoords, currentStore, userProfile, updateItem]);

  useEffect(() => {
    let ignore = false;
    
    if (!slug || !supabase || !userProfile?.id || !isUltra) return;
    
    const timer = setTimeout(() => checkLattice(ignore), 800);
    const interval = setInterval(() => checkLattice(ignore), 60000);

    return () => {
      ignore = true;
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [slug, currentPrice, itemInStore?.embedding, itemInStore?.canonicalId, supabase, userProfile?.id, isUltra, currentStore?.name, itemId, operatingContext, checkLattice]);

  // IMPORTANT: We render the motion div conditionally but the hook logic above ALWAYS runs
  return (
    <AnimatePresence>
      {(status || isScanning) && (
        <motion.div 
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          onClick={(e) => { 
            e.preventDefault();
            e.stopPropagation(); 
            if (status) onToggleExpand?.(!isExpanded);
            else checkLattice(false);
          }}
          title={status === 'beater'
            ? t('history.lattice_beater', { defaultValue: 'Lattice-Beater' })
            : status === 'check'
              ? t('lattice_status.check_price', { defaultValue: 'Check price' })
              : undefined}
          className={cn(
            "flex items-center justify-center w-6 h-6 rounded-full border transition-all cursor-pointer",
            isScanning && !status ? "bg-gray-50 border-gray-200 text-gray-400 cursor-wait" :
            status === 'beater' ? (isExpanded ? "bg-emerald-500 border-emerald-600 text-white shadow-inner" : "bg-emerald-50 border-emerald-100 text-emerald-600 hover:scale-110 active:scale-95 shadow-sm") :
            status === 'check' ? (isExpanded ? "bg-amber-500 border-amber-600 text-white shadow-inner" : "bg-amber-50 border-amber-200 text-amber-600 hover:scale-110 active:scale-95 shadow-sm") :
            isExpanded ? "bg-indigo-500 border-indigo-600 text-white shadow-inner" :
            "bg-indigo-50 border-indigo-100 text-indigo-500 hover:scale-110 active:scale-95 shadow-sm"
          )}
        >
          {isScanning && !status ? (
            <Loader2 size={10} className="animate-spin" />
          ) : status === 'beater' ? (
            <Trophy size={12} />
          ) : status === 'check' ? (
            <AlertTriangle size={12} />
          ) : (
            <Sparkles size={12} className={!isExpanded ? "animate-pulse" : ""} />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};
